import { ArrowNarrowLeftIcon } from "Icons/ArrowNarrowLeft";
import { LoadingAltIcon } from "Icons/LoadingAlt";

import { useInView } from "react-intersection-observer";

import { css } from "@emotion/react";

import { ButtonLink } from "components/shared/library/ButtonLink";
import { SpinningIcon } from "components/shared/library/SpinningIcon";
import { Typography } from "components/shared/library/Typography";

import { JobPreview } from "apis/jobs";

import { useJobSearch } from "utils/jobsSearch";
import { down } from "utils/mediaQueryStrings";
import { getJobsUrl } from "utils/urls";

import { uiGray, white } from "constants/colors";

import { JobPreviewCard } from "./JobPreviewCard";

const style = {
  container: css({
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    border: `1px solid ${uiGray[10]}`,
    background: white,
    width: 400,
    minWidth: 400,
    height: "calc(100vh - 165px)", // this 165px offset is the approximate height of the searchbar filter and the top nav
    [down("mobile")]: {
      width: "-webkit-fill-available",
      minWidth: 200,
    },
    overflow: "auto",
  }),
  header: css({
    borderBottom: `1px solid ${uiGray[10]}`,
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    position: "sticky",
    top: 0,
    background: white,
    zIndex: 1,
    gap: 8,
  }),
  headerContent: css({
    display: "flex",
    flexDirection: "column",
  }),
  jobListContainer: css({
    overflow: "auto",
    maxHeight: "calc(100% - 60px)",
  }),
  loadingIconContainer: css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 10,
  }),
};

export const JobList = ({
  activeJobId,
  jobs,
  loadMoreJobs,
  isLoadingMore,
  count,
  onPreviewCardClick,
}: {
  activeJobId: string | undefined;
  jobs: JobPreview[];
  loadMoreJobs: () => void;
  isLoadingMore: boolean;
  count: number | undefined;
  onPreviewCardClick: () => void;
}) => {
  const { group, upsertJobIdValue } = useJobSearch();

  const { ref } = useInView({
    threshold: 0.1,
    onChange: (inView) => {
      if (inView) loadMoreJobs();
    },
  });

  return (
    <div css={style.container}>
      <div css={style.header}>
        <ButtonLink
          ariaLabel="Go to jobs home"
          IconLeft={ArrowNarrowLeftIcon}
          color="black"
          background="ghost"
          size="medium"
          isSquare
          href={getJobsUrl()}
          sameTab
        />
        <div css={style.headerContent}>
          <Typography size="S" weight="bold">
            {group || "All jobs"}
          </Typography>
          <Typography size="XS">Best matching jobs for you</Typography>
          <Typography size="XXS">{count} results</Typography>
        </div>
      </div>
      <div css={style.jobListContainer}>
        {jobs.map((job) => (
          <JobPreviewCard
            key={job.jobId}
            onClick={() => {
              upsertJobIdValue(job.jobId);
              onPreviewCardClick();
            }}
            job={job}
            isActive={job.jobId === activeJobId}
          />
        ))}
        <div ref={ref} css={style.loadingIconContainer}>
          {isLoadingMore && <SpinningIcon Icon={LoadingAltIcon} />}
        </div>
      </div>
    </div>
  );
};
